import { Dialog as ReakitDialog, DialogBackdrop, DialogStateReturn } from 'reakit/Dialog';
import { cx } from '@linaria/core';
import { useCallback, useEffect, useRef } from 'react';
import { IconCloseRound } from 'components/Icons';
import getConfig from 'next/config';
import { Button } from 'components/Button';
import { noop } from 'utils/fp';
import { useRouter } from 'next/router';
import { useUserNeeds2FA } from 'modules/Auth/hooks';
import { PendingVerification } from 'modules/Auth/components/PendingVerification';
import { ForgotPassword } from 'modules/Auth/components/RestorePassword/ForgotPassword/ForgotPassword';
import { TwoFactorLoginForm } from 'modules/Auth/components/TwoFactorAuth/TwoFactorLoginForm';
import { useLoginRouteHandler } from 'modules/Auth/useLoginRouteHandler';
import { AuthModalState } from 'modules/Auth/constant';
import { SignInView } from './SignInView';
import { SignUpView } from './SignUpView';
import { dialogStyles, backdropStyles, closeButtonStyles, forgotPasswordStyles, TwoFAStyles } from './styles';

export const AuthModal = ({
  dialog,
  state,
  setState,
  authData,
  onAuth,
}: {
  dialog: DialogStateReturn;
  state: AuthModalState;
  setState: (state: AuthModalState) => void;
  authData?: CF.LocalUserWithOrg;
  onAuth?: (signup?: boolean) => void;
}): JSX.Element => {
  const router = useRouter();
  const { asPath } = router;
  const { publicRuntimeConfig } = getConfig();
  const { handleLoginRedirection } = useLoginRouteHandler();

  const title = 'Log in to Clarifai';

  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (dialog.visible) {
      ref.current?.focus();
    }
  }, [dialog.visible]);

  const userNeeds2FA = useUserNeeds2FA();

  useEffect(() => {
    if (userNeeds2FA) {
      setState(AuthModalState.SHOW_2FA);
    } else if (state === AuthModalState.SHOW_2FA) {
      dialog.hide();
    }
  }, [userNeeds2FA, state]);

  const resetLoginMode = useCallback(() => {
    setState(AuthModalState.SHOW_SIGN_IN);
  }, []);

  const onSignIn = useCallback(() => {
    onAuth?.();
    dialog.hide();
    handleLoginRedirection();
  }, []);

  const onSignUp = useCallback(() => {
    setState(AuthModalState.SHOW_PENDING_VERIFICATION);
    onAuth?.(true);
    handleLoginRedirection();
  }, [asPath]);

  return (
    <DialogBackdrop
      {...dialog}
      className={cx(backdropStyles, 'reakit-dialog-backdrop', state === AuthModalState.SHOW_PENDING_VERIFICATION && 'blurry')}
      data-testid="authDialogBackdrop"
      onMouseDown={() => noop()}
    >
      {(!authData || state === AuthModalState.SHOW_PENDING_VERIFICATION || state === AuthModalState.SHOW_2FA) && (
        <ReakitDialog
          hideOnClickOutside={false}
          hideOnEsc={false}
          className={cx(dialogStyles, 'reakit-dialog')}
          {...dialog}
          aria-label={title}
          ref={ref}
        >
          {state !== AuthModalState.SHOW_PENDING_VERIFICATION && (
            <Button
              variant="tertiary"
              className={closeButtonStyles}
              onClick={() => {
                dialog.hide();
              }}
            >
              <IconCloseRound size={24} />
            </Button>
          )}
          {state === AuthModalState.SHOW_2FA && (
            <div className={TwoFAStyles}>
              <TwoFactorLoginForm onBackClick={resetLoginMode} onSignIn={onSignIn} />
            </div>
          )}
          {state === AuthModalState.SHOW_FORGOT_PASSWORD && (
            <div className={forgotPasswordStyles}>
              <ForgotPassword onBackClick={() => setState(AuthModalState.SHOW_SIGN_IN)} />
            </div>
          )}
          {state === AuthModalState.SHOW_SIGN_IN && (
            <SignInView
              onForgotPasswordClick={() => setState(AuthModalState.SHOW_FORGOT_PASSWORD)}
              onSignUpClick={() => setState(AuthModalState.SHOW_SIGN_UP)}
              onSignin={onSignIn}
            />
          )}
          {state === AuthModalState.SHOW_SIGN_UP && (
            <SignUpView
              onSignUp={onSignUp}
              onSignInClick={() => setState(AuthModalState.SHOW_SIGN_IN)}
              siteKey={publicRuntimeConfig.NEXT_PUBLIC_GOOGLE_RECAPTCHA_SITE_KEY}
              defaultCaptchaToken={publicRuntimeConfig.NEXT_PUBLIC_DEFAULT_CAPTCHA_TOKEN}
            />
          )}
          {state === AuthModalState.SHOW_PENDING_VERIFICATION && <PendingVerification />}
        </ReakitDialog>
      )}
    </DialogBackdrop>
  );
};

export { AuthModalState };
